import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Loader2, Send, MessageSquare, ChevronDown, ChevronUp, Plus, ArrowLeft } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Messages = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const res = await axios.get(`${API}/my-messages`);
      setMessages(res.data);
    } catch (error) {
      console.error('Failed to fetch messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReply = async (messageId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a message');
      return;
    }
    setReplying(true);
    try {
      await axios.post(`${API}/messages/${messageId}/reply`, { message: replyText.trim() });
      toast.success('Reply sent!');
      setReplyText('');
      fetchMessages();
    } catch (error) {
      toast.error('Failed to send reply');
    } finally {
      setReplying(false);
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(expandedId === id ? null : id);
    setReplyText('');
  };

  const hasUnreadReplies = (msg) => {
    return msg.replies && msg.replies.length > 0 && msg.replies.some(r => r.sender === 'admin');
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="my-messages-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-3xl font-bold text-foreground">My Messages</h1>
          <p className="text-muted-foreground mt-1">
            {messages.length === 0 ? 'No conversations yet' : `${messages.length} conversation${messages.length !== 1 ? 's' : ''}`}
          </p>
        </div>
        <Link to="/contact">
          <Button className="btn-accent" data-testid="new-message-btn">
            <Plus className="h-4 w-4 mr-2" />
            New Message
          </Button>
        </Link>
      </div>

      {messages.length === 0 ? (
        <div className="text-center py-20">
          <MessageSquare className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
          <h2 className="font-serif text-xl font-bold text-foreground mb-2">No messages yet</h2>
          <p className="text-muted-foreground mb-6">
            Have a question? Send us a message and we'll get back to you.
          </p>
          <Link to="/contact">
            <Button className="btn-accent">
              <Send className="h-4 w-4 mr-2" />
              Contact Us
            </Button>
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {messages.map(msg => (
            <Card
              key={msg.id}
              className={`transition-all ${hasUnreadReplies(msg) ? 'border-primary/50' : ''}`}
              data-testid={`message-thread-${msg.id}`}
            >
              <div
                className="p-4 cursor-pointer"
                onClick={() => toggleExpand(msg.id)}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="font-semibold text-foreground">{msg.subject}</h3>
                      {msg.replies && msg.replies.length > 0 && (
                        <Badge variant="outline" className="text-xs">
                          {msg.replies.length} {msg.replies.length === 1 ? 'reply' : 'replies'}
                        </Badge>
                      )}
                      {hasUnreadReplies(msg) && (
                        <Badge className="bg-primary text-primary-foreground text-xs">New reply</Badge>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-1">{msg.message}</p>
                    <span className="text-xs text-muted-foreground mt-1 block">
                      {new Date(msg.updated_at || msg.created_at).toLocaleDateString()} at{' '}
                      {new Date(msg.updated_at || msg.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}
                    </span>
                  </div>
                  <div className="flex-shrink-0">
                    {expandedId === msg.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>
              </div>

              {/* Expanded thread */}
              {expandedId === msg.id && (
                <CardContent className="pt-0 border-t">
                  <div className="py-4 space-y-4">
                    {/* Original message */}
                    <div className="bg-primary/5 rounded-lg p-4 border-l-4 border-primary">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium bg-primary/20 text-primary px-2 py-0.5 rounded">You</span>
                        <span className="text-xs text-muted-foreground">{new Date(msg.created_at).toLocaleString()}</span>
                      </div>
                      <p className="text-sm whitespace-pre-wrap">{msg.message}</p>
                    </div>

                    {/* Replies */}
                    {msg.replies && msg.replies.map(reply => (
                      <div
                        key={reply.id}
                        className={`rounded-lg p-4 ${
                          reply.sender === 'admin'
                            ? 'bg-muted/50 ml-0 mr-4'
                            : 'bg-primary/5 border-l-4 border-primary ml-4'
                        }`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded ${
                            reply.sender === 'admin'
                              ? 'bg-amber-100 text-amber-800'
                              : 'bg-primary/20 text-primary'
                          }`}>
                            {reply.sender === 'admin' ? 'Brookzone Support' : 'You'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {new Date(reply.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                      </div>
                    ))}

                    {/* Reply input */}
                    <div className="flex gap-2 pt-2" data-testid={`customer-reply-form-${msg.id}`}>
                      <Input
                        placeholder="Type your reply..."
                        value={expandedId === msg.id ? replyText : ''}
                        onChange={(e) => setReplyText(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleReply(msg.id)}
                        disabled={replying}
                        data-testid={`customer-reply-input-${msg.id}`}
                      />
                      <Button
                        onClick={() => handleReply(msg.id)}
                        disabled={replying || !replyText.trim()}
                        className="btn-accent flex-shrink-0"
                        data-testid={`customer-reply-send-${msg.id}`}
                      >
                        {replying ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Send className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default Messages;
